import {
	COLUMN_WIDTH,
	ROW_HEADER_HEIGHT,
	ROW_HEIGHT,
} from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import {
	SecurityRowMenuDropdown,
	SecurityRowMenuItem,
} from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/BreakpointSummaryTable/styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { BreakpointSummaryPayload, CurrencyType } from '@dealstack/shared/lib/generated';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { ActionIcon, Menu, Text } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef } from 'react';
import { SecurityComponentInfo } from '@dealstack/frontend/app/pages/Valuations/shared/securityComponentInfo.types';
import { useBreakpointSummaryTableColumns } from './BreakpointSummaryTable.utils';
import { BreakpointSummaryRow, BreakpointSummaryTransformer } from './BreakpointSummaryTransformer';

interface Props {
	summaryData: BreakpointSummaryPayload | undefined;
	currency: CurrencyType;
	onOpenOverrideDrawer: (info: SecurityComponentInfo) => void;
	onDeleteOverride: (overrideId: string) => void;
}

export const BreakpointSummaryTable: React.FC<Props> = ({
	summaryData,
	currency,
	onOpenOverrideDrawer,
	onDeleteOverride,
}) => {
	const tValuations = useTranslationValuations();
	const tableContainerRef = useRef<HTMLDivElement>(null);

	const transformer = useMemo(
		() => new BreakpointSummaryTransformer(tValuations, currency),
		[tValuations, currency]
	);

	const transformedData = useMemo(() => {
		return transformer.toTableData(summaryData);
	}, [summaryData, transformer]);

	const columns = useBreakpointSummaryTableColumns({
		openSecurityOverrideDrawer: onOpenOverrideDrawer,
		currency,
	});

	const table = useReactTable<BreakpointSummaryRow>({
		data: transformedData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		enableColumnResizing: true,
		columnResizeMode: 'onChange',
		defaultColumn: {
			minSize: COLUMN_WIDTH,
		},
	});

	const { rows } = table.getRowModel();
	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => tableContainerRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 5,
	});
	const virtualRows = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();

	const renderRowActions = (row: BreakpointSummaryRow) => {
		const securityId = row.metadata.securityId;
		const securityComponent = row.metadata.securityComponent;
		const trancheKey = row.metadata.trancheKey;

		if (!(securityId && securityComponent)) {
			return <ActionIcon size="md" variant="transparent" sx={{ cursor: 'default' }} />;
		}

		return (
			<Menu zIndex={Z_INDEX.MENU} withinPortal position="left-start">
				<Menu.Target>
					<ActionIcon size="md" variant="transparent">
						<IconDotsVertical />
					</ActionIcon>
				</Menu.Target>
				<SecurityRowMenuDropdown>
					<Menu.Item
						onClick={() => {
							if (securityId && securityComponent) {
								onOpenOverrideDrawer({
									securityId,
									component: securityComponent,
									trancheKey,
								});
							}
						}}
						sx={(theme) => ({
							'&:hover': {
								backgroundColor: theme.colors.gray[2],
							},
						})}
					>
						<SecurityRowMenuItem>
							<Icon variant="pencil-edit-line" size="md" />
							<Text size="B6Medium">
								{tValuations('breakpoint_summary.add_override_button')}
							</Text>
						</SecurityRowMenuItem>
					</Menu.Item>
					{row.metadata.overrideId && (
						<Menu.Item
							onClick={() => {
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- overrideId is guaranteed to be present because of the conditional render
								onDeleteOverride(row.metadata.overrideId!);
							}}
							sx={(theme) => ({
								'&:hover': {
									backgroundColor: theme.colors.gray[2],
								},
							})}
						>
							<SecurityRowMenuItem>
								<Icon variant="trash-can" size="md" />
								<Text size="B6Medium">
									{tValuations('breakpoint_summary.delete_override_button')}
								</Text>
							</SecurityRowMenuItem>
						</Menu.Item>
					)}
				</SecurityRowMenuDropdown>
			</Menu>
		);
	};

	return (
		<div
			ref={tableContainerRef}
			style={{
				overflow: 'auto',
				margin: 0,
				padding: 0,
				borderRadius: '0.25rem',
				border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
			}}
		>
			<table
				data-testid={VALUATION_ELEMENT_ID.BREAKPOINT_TABLE_SPACE}
				style={{
					display: 'grid',
					height: '100%',
					width: '100%',
					gridTemplateRows: 'auto 1fr',
					border: 'none',
					borderCollapse: 'collapse',
					borderSpacing: 0,
				}}
			>
				<thead
					style={{
						display: 'grid',
						position: 'sticky',
						top: 0,
						zIndex: Z_INDEX.STICKY_TABLE_HEADER,
					}}
				>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr
							key={headerGroup.id}
							style={{
								display: 'flex',
								minHeight: `${ROW_HEADER_HEIGHT}px`,
								boxSizing: 'border-box',
							}}
						>
							{headerGroup.headers.map((header) =>
								header.isPlaceholder ? null : (
									<th
										key={header.id}
										style={{
											display: 'flex',
											minWidth: COLUMN_WIDTH,
											flex: '1 1 0px',
											backgroundColor: defaultTheme.v3.colors.gray[2],
											borderLeft: 'none',
											borderRight: 'none',
											borderBottom: 'none',
											minHeight: '3.25rem',
											position: 'relative',
										}}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
										{/* Resizing handle */}
										<div
											{...{
												onMouseDown: header.getResizeHandler(),
												onTouchStart: header.getResizeHandler(),
											}}
											style={{
												position: 'absolute',
												right: 0,
												top: 0,
												height: '100%',
												width: '4px',
												backgroundColor: 'transparent',
												cursor: 'col-resize',
												userSelect: 'none',
												touchAction: 'none',
											}}
										/>
									</th>
								)
							)}
							{/* Actions column header */}
							<th
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '48px',
									minWidth: '48px',
									maxWidth: '48px',
									backgroundColor: defaultTheme.v3.colors.gray[2],
									borderLeft: 'none',
									borderRight: 'none',
									borderBottom: 'none',
									minHeight: '3.25rem',
								}}
							/>
						</tr>
					))}
				</thead>

				<tbody
					style={{
						display: 'grid',
						height: `${totalSize}px`,
						position: 'relative',
					}}
				>
					{virtualRows.map((virtualRow) => {
						const row = rows[virtualRow.index];

						return (
							<tr
								key={row.id}
								data-index={virtualRow.index}
								data-testindex={virtualRow.index}
								ref={(node) => rowVirtualizer.measureElement(node)}
								style={{
									display: 'flex',
									position: 'absolute',
									transform: `translateY(${virtualRow.start}px)`,
									width: '100%',
									minHeight: `${ROW_HEIGHT}px`,
									maxHeight: `${ROW_HEIGHT}px`,
									backgroundColor: row.original.metadata?.isTotal
										? defaultTheme.v3.colors.gray[2]
										: 'transparent',
								}}
							>
								{row.getVisibleCells().map((cell, cellIndex) => {
									const isLastRow = virtualRow.index === rows.length - 1;
									return (
										<td
											key={cell.id}
											data-testindex={cellIndex}
											style={{
												display: 'flex',
												minWidth: COLUMN_WIDTH,
												flex: '1 1 0px',
												borderRight: 'none',
												borderLeft: 'none',
												borderBottom: isLastRow
													? 'none'
													: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</td>
									);
								})}
								{/* Actions column cell */}
								<td
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '48px',
										minWidth: '48px',
										maxWidth: '48px',
										borderRight: 'none',
										borderLeft: 'none',
										borderBottom:
											virtualRow.index === rows.length - 1
												? 'none'
												: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									}}
								>
									{renderRowActions(row.original)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
